import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Printer, Calculator, TrendingUp, DollarSign } from "lucide-react";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n || 0));
}
function money(n, big = false) {
  const v = n || 0;
  if (big && v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (big && v >= 10_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${fmt(v)}`;
}

export default function ROICalculatorPage() {
  const [monthlyROs, setMonthlyROs] = useState(800);
  const [declineRate, setDeclineRate] = useState(30); // % of ROs with a declined line
  const [avgTicket, setAvgTicket] = useState(400);
  const [convRate, setConvRate] = useState(8); // % of texted customers who book & pay
  const [monthlyFee, setMonthlyFee] = useState(1599);
  const [onboardingFee, setOnboardingFee] = useState(999);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setMonthlyFee(Number(r.data?.monthly_fee ?? 1599));
        setOnboardingFee(Number(r.data?.onboarding_fee ?? 999));
      })
      .catch(() => {});
  }, []);

  const nums = useMemo(() => {
    const monthlyDeclines = monthlyROs * (declineRate / 100);
    const unrealized = monthlyDeclines * avgTicket;
    const recoveredCustomers = monthlyDeclines * (convRate / 100);
    const grossRecovered = recoveredCustomers * avgTicket;
    const yearlyGross = grossRecovered * 12;
    const yearlyDDCost = monthlyFee * 12 + onboardingFee;
    const roi = yearlyDDCost > 0 ? yearlyGross / yearlyDDCost : 0;
    return { monthlyDeclines, unrealized, recoveredCustomers, grossRecovered, yearlyGross, yearlyDDCost, roi };
  }, [monthlyROs, declineRate, avgTicket, convRate, monthlyFee, onboardingFee]);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="ROI Calculator" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Tool</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            ROI Calculator
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Sit next to the dealer principal, punch in their real numbers, and show them the exact dollars
            sitting on their shelf every month — and how much they walk away with after Decline Drive.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-roi-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <Card className="p-6 border-border" data-testid="roi-inputs">
          <div className="flex items-center gap-2 mb-1">
            <Calculator className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
            <div className="font-mono-label text-muted-foreground">Their Numbers</div>
          </div>
          <div className="font-display text-xl font-bold tracking-tight mb-5">
            Type in what the dealer tells you
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-mono-label">Repair orders per month</Label>
                <div className="font-display text-lg font-bold tabular-nums">{fmt(monthlyROs)}</div>
              </div>
              <Slider
                min={200}
                max={5000}
                step={50}
                value={[monthlyROs]}
                onValueChange={(v) => setMonthlyROs(v[0])}
                data-testid="roi-input-ros"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Industry avg: 800 – 2,500 (single franchised rooftop).
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-mono-label">Decline rate (RO's with a declined line)</Label>
                <div className="font-display text-lg font-bold tabular-nums">{declineRate}%</div>
              </div>
              <Slider
                min={10}
                max={60}
                step={1}
                value={[declineRate]}
                onValueChange={(v) => setDeclineRate(v[0])}
                data-testid="roi-input-decline-rate"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Industry avg: 30 – 45% (Reynolds + CDK service benchmarks).
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-mono-label">Avg declined-service ticket</Label>
                <div className="font-display text-lg font-bold tabular-nums">${fmt(avgTicket)}</div>
              </div>
              <Slider
                min={100}
                max={2000}
                step={25}
                value={[avgTicket]}
                onValueChange={(v) => setAvgTicket(v[0])}
                data-testid="roi-input-avg-ticket"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Industry avg: $350 – $800.
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-mono-label">Decline Drive conversion rate</Label>
                <div className="font-display text-lg font-bold tabular-nums">{convRate}%</div>
              </div>
              <Slider
                min={3}
                max={20}
                step={1}
                value={[convRate]}
                onValueChange={(v) => setConvRate(v[0])}
                data-testid="roi-input-conv-rate"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Conservative: 8%. Realistic: 10 – 12%. Aggressive: 15%.
              </div>
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-4">
          <Card className="p-6 border-border bg-navy text-white" data-testid="roi-unrealized">
            <div className="font-mono-label text-[hsl(var(--gold-light))] mb-1">Recoverable pool</div>
            <div className="font-display text-sm text-white/80 mb-2">Total declined service revenue on the shelf (pre-recovery)</div>
            <div className="font-display text-5xl font-black text-[hsl(var(--gold))] tabular-nums">
              {money(nums.unrealized, true)}
              <span className="text-xl text-white/60 font-normal ml-2">/mo</span>
            </div>
            <div className="text-sm text-white/70 mt-2 tabular-nums">
              = {money(nums.unrealized * 12, true)} annually · Realistically recover ~{convRate}% of this
            </div>
          </Card>

          <Card className="p-6 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5" data-testid="roi-recovered">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
              <div className="font-mono-label text-muted-foreground">You recover for them</div>
            </div>
            <div className="font-display text-3xl font-black tabular-nums mt-2">
              {money(nums.grossRecovered, true)}
              <span className="text-base text-muted-foreground font-normal ml-2">/mo</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1 tabular-nums">
              = {money(nums.yearlyGross, true)} per year · {fmt(nums.recoveredCustomers)} customers back on the drive
            </div>
          </Card>

          <Card className="p-6 border-border" data-testid="roi-cost">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
              <div className="font-mono-label text-muted-foreground">Their investment</div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">Monthly</div>
                <div className="font-display text-xl font-bold tabular-nums">${fmt(monthlyFee)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Onboarding (year 1)</div>
                <div className="font-display text-xl font-bold tabular-nums">${fmt(onboardingFee)}</div>
              </div>
              <div className="col-span-2 pt-2 border-t border-border">
                <div className="text-muted-foreground text-xs">Total year 1</div>
                <div className="font-display text-2xl font-bold tabular-nums">${fmt(nums.yearlyDDCost)}</div>
              </div>
              <div className="col-span-2 text-[0.6rem] text-muted-foreground italic pt-1">
                Change these in Settings → Billing &amp; Recurring Revenue.
              </div>
            </div>
          </Card>

          <Card className="p-6 border-border bg-emerald-600 text-white" data-testid="roi-multiple">
            <div className="font-mono-label text-emerald-100 mb-1">Their ROI</div>
            <div className="font-display text-6xl font-black tabular-nums">
              {nums.roi.toFixed(1)}×
            </div>
            <div className="text-sm text-emerald-50 mt-2">
              Every $1 they pay you comes back as ${nums.roi.toFixed(2)} in recovered service revenue.
            </div>
          </Card>
        </div>
      </div>

      {/* Pitch line */}
      <Card className="p-6 border-border mt-6 bg-muted/40" data-testid="roi-pitch">
        <div className="font-mono-label text-muted-foreground mb-2">The Close</div>
        <p className="font-display italic text-lg leading-relaxed text-foreground/90">
          &quot;You&apos;re already paying for these customers — they walked in, sat in your service lounge,
          and were told they need work. They said &apos;not today.&apos; Every one is a paying customer
          already, they just needed a second touch. Show me another investment where {money(nums.yearlyDDCost)}
          brings back {money(nums.yearlyGross, true)}. That&apos;s a {nums.roi.toFixed(1)}× return.&quot;
        </p>
      </Card>
    </DashboardShell>
  );
}
